/**
 * MFE Helper Utilities
 *
 * Utilities for working with microfrontends in the shell application
 */

/**
 * Determines if a component is a remote MFE component
 * MFE components are lazy-loaded via Module Federation
 *
 * @param {React.ComponentType} component - The component to check
 * @returns {boolean} True if component is an MFE
 */
export const isMFEComponent = (component) => {
  if (!component) return false;

  // Check if it's a lazy component (has $$typeof and _payload)
  if (component.$$typeof && component._payload) {
    return true;
  }

  // Check if it has lazy loading characteristics
  if (typeof component === 'object' && component._init) {
    return true;
  }

  return false;
};

/**
 * Extracts MFE name from component
 * Attempts to derive a meaningful name from the lazy component
 *
 * @param {React.ComponentType} component - The component
 * @param {string} fallbackName - Fallback name if detection fails
 * @returns {string} The MFE name
 */
export const getMFEName = (component, fallbackName = 'Microfrontend') => {
  if (!component) return fallbackName;

  // Try to extract from display name
  if (component.displayName) {
    return component.displayName;
  }

  // Try to extract from lazy component payload
  if (component._payload && component._payload._result) {
    const result = component._payload._result;
    if (result && result.name) {
      return result.name;
    }
  }

  return fallbackName;
};

/**
 * Registry of known MFE components
 * Maps component names to their MFE metadata
 */
const MFE_REGISTRY = {
  'TemplatePage': { mfeName: 'Template', source: 'microTemplate' },
  'AdministrationPage': { mfeName: 'Administration', source: 'microAdministration' },
  'RolesPage': { mfeName: 'Administration', source: 'microAdministration' },
  'OrganizationUnitsPage': { mfeName: 'Administration', source: 'microAdministration' },
  'EditionsPage': { mfeName: 'Administration', source: 'microAdministration' },
  'MicroTestPage': { mfeName: 'Test', source: 'microTest' },
};

/**
 * Gets MFE metadata for a component name
 *
 * @param {string} componentName - Name of the component
 * @returns {Object|null} MFE metadata or null if not found
 */
export const getMFEMetadata = (componentName) => {
  return MFE_REGISTRY[componentName] || null;
};

/**
 * Checks if a component should be wrapped with error boundary
 *
 * @param {React.ComponentType} component - The component to check
 * @param {string} componentName - Name of the component
 * @returns {boolean} True if component should be wrapped
 */
export const shouldWrapWithErrorBoundary = (component, componentName) => {
  // Always wrap lazy-loaded MFE components
  if (isMFEComponent(component)) {
    return true;
  }

  // Check if component is registered as an MFE
  if (componentName && getMFEMetadata(componentName)) {
    return true;
  }

  return false;
};
